import React, { useEffect, useRef, useState } from 'react';
import { Check, Code2, Copy } from 'lucide-react';
import 'highlight.js/styles/github-dark.css';
import { highlightToHtml } from './highlightLoader';

// Blocco codice condiviso (chat markdown + artefatti `code`/sorgenti):
// syntax highlighting lazy (highlight.js) + bottone copia con feedback.
// Streaming-safe: mentre l'highlight async è in volo mostra il testo semplice
// con la STESSA metrica tipografica, così il flip non causa salti di layout.

interface CodeBlockProps {
  code: string;
  language?: string;
  /** Header con etichetta linguaggio + copia (default true). */
  showHeader?: boolean;
  className?: string;
}

export const CopyButton: React.FC<{ text: string; className?: string; title?: string }> = ({ text, className, title }) => {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<number | null>(null);
  useEffect(() => () => { if (timerRef.current !== null) window.clearTimeout(timerRef.current); }, []);
  return (
    <button
      type="button"
      title={title ?? 'Copia'}
      aria-label={title ?? 'Copia'}
      className={className ?? 'inline-flex items-center gap-1 rounded px-1.5 py-1 text-[11px] text-slate-300 hover:text-white hover:bg-white/10 transition-colors'}
      onClick={() => {
        navigator.clipboard.writeText(text).then(() => {
          setCopied(true);
          if (timerRef.current !== null) window.clearTimeout(timerRef.current);
          timerRef.current = window.setTimeout(() => setCopied(false), 1500);
        }).catch(() => {});
      }}
    >
      {copied ? <Check className="h-3 w-3 text-emerald-400" /> : <Copy className="h-3 w-3" />}
      {copied ? 'Copiato' : 'Copia'}
    </button>
  );
};

export const CodeBlock: React.FC<CodeBlockProps> = ({ code, language, showHeader = true, className }) => {
  const [html, setHtml] = useState<string | null>(null);
  const [resolvedLang, setResolvedLang] = useState<string>(language ?? 'text');

  useEffect(() => {
    let cancelled = false;
    highlightToHtml(code, language).then(res => {
      if (cancelled) return;
      if (res) {
        setHtml(res.html);
        setResolvedLang(language?.trim() ? language.trim().toLowerCase() : res.language);
      } else {
        setHtml(null);
      }
    });
    return () => { cancelled = true; };
  }, [code, language]);

  return (
    <div className={`not-prose group/code rounded-lg overflow-hidden bg-[#0d1117] border border-slate-800 ${className ?? ''}`}>
      {showHeader && (
        <div className="flex items-center justify-between px-3 py-1.5 border-b border-slate-800 bg-slate-900/80">
          <div className="flex items-center gap-1.5 text-[11px] text-slate-400 font-medium">
            <Code2 className="h-3 w-3" />
            <span className="uppercase tracking-wide">{resolvedLang}</span>
          </div>
          <CopyButton text={code} />
        </div>
      )}
      <pre className="m-0 p-3 text-xs leading-relaxed overflow-x-auto text-slate-100 bg-transparent">
        {html !== null ? (
          <code
            className={`hljs language-${resolvedLang} !bg-transparent !p-0`}
            // HTML prodotto da highlight.js: input integralmente escapato dalla lib.
            dangerouslySetInnerHTML={{ __html: html }}
          />
        ) : (
          <code className="!bg-transparent !p-0">{code}</code>
        )}
      </pre>
    </div>
  );
};
